import { Input } from "@/components/ui/input";
import { MapPin, ArrowRight, User, Phone, Hash, Clock } from "lucide-react";

const TRIP_TYPES = ["One Way", "Round Trip", "Onward"];

const INPUT_CLS =
  "bg-[#FFFFFF] border border-slate-200 rounded-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-10 px-3";

function IconField({ icon: Icon, label, testId, ...props }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="overline flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-[#2563EB]" /> {label}
      </span>
      <Input data-testid={testId} className={INPUT_CLS} {...props} />
    </label>
  );
}

export default function TripDetails({ trip, onChange }) {
  const set = (field, value) => onChange({ ...trip, [field]: value });

  return (
    <div className="bg-[#F8FAFC] border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="w-4 h-4 text-[#2563EB]" />
        <div className="heading text-sm font-semibold uppercase tracking-wide">Trip Details</div>
        <span className="mono text-[10px] text-slate-400 ml-2">
          Printed on the export header — dispatch-ready
        </span>
      </div>

      {/* Row 1: From / To / Trip Type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
        <IconField
          icon={MapPin}
          label="From (Origin)"
          testId="trip-from"
          placeholder="e.g. Navasheva"
          value={trip.from_location || ""}
          onChange={(e) => set("from_location", e.target.value)}
        />
        <label className="flex flex-col gap-1 relative">
          <span className="overline flex items-center gap-1.5">
            <ArrowRight className="w-3 h-3 text-[#2563EB]" /> To (Destination)
          </span>
          <Input
            data-testid="trip-to"
            className={INPUT_CLS}
            placeholder="e.g. Sabarmati"
            value={trip.to_location || ""}
            onChange={(e) => set("to_location", e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="overline">Trip Type</span>
          <select
            data-testid="trip-type"
            className={INPUT_CLS + " appearance-none cursor-pointer pr-8"}
            value={trip.trip_type || "One Way"}
            onChange={(e) => set("trip_type", e.target.value)}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23FFD600' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 10px center",
            }}
          >
            {TRIP_TYPES.map((t) => (
              <option key={t} value={t} style={{ background: "#FFFFFF", color: "#fff" }}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Row 2: Dispatch meta — driver name / phone / truck # / scheduled at */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <IconField
          icon={User}
          label="Driver Name"
          testId="trip-driver-name"
          placeholder="e.g. Ramesh Kumar"
          value={trip.driver_name || ""}
          onChange={(e) => set("driver_name", e.target.value)}
        />
        <IconField
          icon={Phone}
          label="Driver Phone"
          testId="trip-driver-phone"
          placeholder="e.g. 98765 43210"
          value={trip.driver_phone || ""}
          onChange={(e) => set("driver_phone", e.target.value)}
        />
        <IconField
          icon={Hash}
          label="Vehicle Number"
          testId="trip-vehicle-number"
          placeholder="e.g. MH-14-AB-1234"
          value={trip.vehicle_number || ""}
          onChange={(e) => set("vehicle_number", e.target.value.toUpperCase())}
        />
        <label className="flex flex-col gap-1">
          <span className="overline flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-[#2563EB]" /> Scheduled
          </span>
          <Input
            data-testid="trip-scheduled-at"
            type="datetime-local"
            className={INPUT_CLS + " text-white/90"}
            value={trip.scheduled_at || ""}
            onChange={(e) => set("scheduled_at", e.target.value)}
          />
        </label>
      </div>
    </div>
  );
}
